import useTranslator from "../../hooks/useTranslator";
import { useContext, useMemo, useState } from "react";
import { isValidUrl, streetTypes } from "../../helper/common";
import { Button, Modal, SearchSelect, TextField } from "../design";
import provincesData from "../../Utils/provincia_municipios.json";
import AuthContext from "../../context/auth/AuthContext";
import "./addPropertyModal.css";

const FullAddressFormModal = ({
  isLandlord,
  setIsAddManuallyModal,
  formData,
  handleInputChange,
  handleSubmit,
  setStep,
  onClose,
  isEdit = false,
  submitLoading,
}) => {
  const { _t } = useTranslator();
  const { user } = useContext(AuthContext);
  const [fieldErrors, setFieldErrors] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const prescoringAgentFieldsEnabled =
    user?.current_workspace?.prescoring_agent_fields_enabled === true;

  const requiredFields = useMemo(() => {
    const fields = [
      "street",
      "street_number",
      "postcode",
      "floor",
      "door",
      "province",
      "municipality",
    ];
    if (!isEdit) fields.unshift("street_type");
    return fields;
  }, [isEdit]);

  const fieldErrorKeyMap = {
    street_type: "streetType",
    street: "street",
    street_number: "number",
    postcode: "postCode",
    floor: "floorNumber",
    door: "letter",
    province: "province",
    municipality: "municipality",
  };

  const getFieldErrorMessage = (field) => {
    const label = _t(fieldErrorKeyMap[field]);
    const translatedRequiredSuffix = _t("isRequired");
    const requiredSuffix =
      translatedRequiredSuffix === "isRequired"
        ? "is required"
        : translatedRequiredSuffix;
    return `${label} ${requiredSuffix}`;
  };

  const validateForm = () => {
    const errors = {};
    requiredFields.forEach((field) => {
      if (!String(formData?.[field] ?? "").trim()) {
        errors[field] = true;
      }
    });
    const adUrl = String(formData?.ad_url ?? "").trim();
    if (prescoringAgentFieldsEnabled && adUrl && !isValidUrl(adUrl)) {
      errors.ad_url = true;
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onFormSubmit = (e) => {
    e.preventDefault();
    setHasSubmitted(true);
    if (!validateForm()) return;
    handleSubmit(e);
  };

  const handleFieldChange = (field, value, event) => {
    if (hasSubmitted) {
      const isEmpty = !String(value ?? "").trim();
      setFieldErrors((prev) => ({ ...prev, [field]: isEmpty }));
    }

    if (event) {
      handleInputChange(event);
    } else {
      handleInputChange({
        target: {
          name: field,
          value,
        },
      });
    }
  };

  const handleAdUrlChange = (event) => {
    const { value } = event.target;
    handleInputChange(event);

    if (hasSubmitted) {
      setFieldErrors((prev) => ({
        ...prev,
        ad_url: Boolean(value.trim()) && !isValidUrl(value),
      }));
    }
  };

  // only digits (and editing keys) can be typed in the number fields
  const digitsOnly = (e) => {
    const allowedKeys = [
      "Backspace",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "Delete",
    ];
    if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  // label with a red star for required fields
  const requiredLabel = (key) => (
    <>
      {_t(key)}
      <span className="property-form__required" aria-hidden="true">
        *
      </span>
    </>
  );

  const errorFor = (field) =>
    fieldErrors[field] ? getFieldErrorMessage(field) : undefined;

  const formId = isEdit ? "edit-property-form" : "add-property-full-form";

  return (
    <Modal
      open
      onClose={() => setIsAddManuallyModal(false)}
      size="md"
      align="start"
      actionsLayout="end"
      title={isEdit ? _t("editYourAddress") : _t("whatIsYourAddress")}
      actions={
        <>
          <Button
            variant="secondary"
            size="lg"
            onClick={() => (isEdit ? onClose() : setStep(1))}
          >
            {_t("goBack")}
          </Button>
          <Button size="lg" type="submit" form={formId} loading={submitLoading}>
            {_t("submit")}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className="property-form"
        onSubmit={onFormSubmit}
        noValidate
      >
        {!isEdit && (
          <SearchSelect
            id="property-street-type"
            label={requiredLabel("streetType")}
            value={formData?.street_type || ""}
            options={streetTypes.map((type) => ({
              label: type,
              value: type,
            }))}
            placeholder={_t("enterStreetType")}
            onChange={(value) => handleFieldChange("street_type", value)}
            error={errorFor("street_type")}
          />
        )}
        <div className="property-form__row">
          <TextField
            id="property-street"
            name="street"
            label={requiredLabel("street")}
            value={formData?.street}
            onChange={(e) => handleFieldChange("street", e.target.value, e)}
            placeholder={_t("enterStreet")}
            error={errorFor("street")}
          />
          <TextField
            id="property-street-number"
            name="street_number"
            label={requiredLabel("number")}
            value={formData?.street_number}
            onChange={(e) =>
              handleFieldChange("street_number", e.target.value, e)
            }
            placeholder={_t("enterNumber")}
            onKeyDown={digitsOnly}
            error={errorFor("street_number")}
          />
        </div>
        <div className="property-form__row">
          <TextField
            id="property-postcode"
            name="postcode"
            label={requiredLabel("postCode")}
            value={formData?.postcode}
            onChange={(e) => handleFieldChange("postcode", e.target.value, e)}
            maxLength={5}
            onKeyDown={digitsOnly}
            placeholder={_t("enterPostCode")}
            error={errorFor("postcode")}
          />
          <TextField
            id="property-staircase"
            name="staircase"
            label={_t("stairway")}
            value={formData?.staircase}
            onChange={handleInputChange}
            placeholder={_t("Enter Stairway")}
          />
        </div>
        <div className="property-form__row">
          <TextField
            id="property-floor"
            name="floor"
            label={requiredLabel("floorNumber")}
            value={formData?.floor}
            onChange={(e) => handleFieldChange("floor", e.target.value, e)}
            placeholder={_t("enterFloorNumber")}
            onInput={(e) => {
              let value = e.target.value.replace(/\D/g, "");
              if (value !== "" && Number(value) > 40) {
                value = "40";
              }
              e.target.value = value;
            }}
            onKeyDown={digitsOnly}
            error={errorFor("floor")}
          />
          <TextField
            id="property-door"
            name="door"
            label={requiredLabel("letter")}
            value={formData?.door}
            onChange={(e) => handleFieldChange("door", e.target.value, e)}
            placeholder={_t("enterLetter")}
            error={errorFor("door")}
          />
        </div>
        <div className="property-form__row">
          <SearchSelect
            id="property-province"
            label={requiredLabel("province")}
            value={formData?.province || ""}
            options={provincesData.map((p) => ({
              label: p.Provincia,
              value: p.Provincia,
            }))}
            placeholder={_t("selectProvince")}
            onChange={(value) => {
              handleFieldChange("province", value);
              handleFieldChange("municipality", "");
            }}
            error={errorFor("province")}
          />
          <SearchSelect
            id="property-municipality"
            label={requiredLabel("municipality")}
            value={formData?.municipality || ""}
            options={
              formData?.province
                ? provincesData
                    .find((p) => p.Provincia === formData.province)
                    ?.municipios.map((m) => ({ label: m, value: m })) || []
                : []
            }
            placeholder={
              formData?.province
                ? _t("selectMunicipality")
                : _t("selectProvinceFirst")
            }
            onChange={(value) => handleFieldChange("municipality", value)}
            disabled={!formData?.province}
            error={errorFor("municipality")}
          />
        </div>
        <div className="property-form__row">
          <TextField
            id="property-bedrooms"
            name="bedrooms"
            label={_t("bedrooms")}
            value={formData?.bedrooms}
            onChange={handleInputChange}
            placeholder={_t("enterBedrooms")}
            onInput={(e) => {
              let value = e.target.value.replace(/\D/g, "");
              if (value !== "" && Number(value) > 10) {
                value = "10";
              }
              e.target.value = value;
            }}
            onKeyDown={digitsOnly}
          />
          <TextField
            id="property-bathrooms"
            name="washrooms"
            label={_t("bathrooms")}
            value={formData?.washrooms}
            onChange={handleInputChange}
            placeholder={_t("enterBathrooms")}
            onInput={(e) => {
              let value = e.target.value.replace(/\D/g, "");
              if (value !== "" && Number(value) > 10) {
                value = "10";
              }
              e.target.value = value;
            }}
            onKeyDown={digitsOnly}
          />
        </div>
        {prescoringAgentFieldsEnabled && (
          <div className="property-form__row">
            <TextField
              id="property-ad-url"
              type="url"
              name="ad_url"
              label={_t("adUrl")}
              value={formData?.ad_url ?? ""}
              onChange={handleAdUrlChange}
              placeholder={_t("enterAdUrl")}
              error={fieldErrors.ad_url ? _t("invalidUrl") : undefined}
            />
            <TextField
              id="property-calendar-id"
              name="calendar_id"
              label={_t("calendarId")}
              value={formData?.calendar_id ?? ""}
              onChange={handleInputChange}
              placeholder={_t("enterCalendarId")}
            />
          </div>
        )}
        <TextField
          id="property-reference"
          name="property_reference"
          label={_t("propertyReferenceOptional")}
          value={formData?.property_reference}
          onChange={handleInputChange}
          placeholder={_t("enterPropertyReference")}
        />
      </form>
    </Modal>
  );
};

export default FullAddressFormModal;
